import { useEffect, useRef, useState } from "react"
import { downloadNestAttachment } from "../../nest/attachments"
import type { ThreadContentPart } from "../../store/SessionThreadModel"
import { ImageLightbox } from "../ui/ImageLightbox"

type ThreadImagePart = Extract<ThreadContentPart, { type: "image" }>
const IMAGE_LOAD_ROOT_MARGIN = "640px 320px"

export function ThreadImages({ images }: { images: readonly ThreadImagePart[] }) {
    if (images.length === 0) return null
    return (
        <div
            data-thread-image-bar
            className="-mx-3 -mb-1.5 mt-2 flex gap-2 overflow-x-auto border-t border-border/70 bg-base-100/20 px-3 py-2 first:mt-0 first:border-t-0"
        >
            {images.map((image, index) => (
                <ThreadImage key={imageKey(image, index)} image={image} />
            ))}
        </div>
    )
}

function ThreadImage({ image }: { image: ThreadImagePart }) {
    const buttonRef = useRef<HTMLButtonElement>(null)
    const [nearViewport, setNearViewport] = useState(() => typeof IntersectionObserver === "undefined")
    const [attachmentURL, setAttachmentURL] = useState<string | null>(null)
    const [lightboxOpen, setLightboxOpen] = useState(false)
    const attachmentId = image.source.type === "nest_attachment" ? image.source.attachmentId : null
    const shouldLoad = nearViewport || lightboxOpen
    const loadedURL = shouldLoad ? (providerImageURL(image) ?? attachmentURL) : null

    useEffect(() => {
        const target = buttonRef.current
        if (!target || typeof IntersectionObserver === "undefined") {
            setNearViewport(true)
            return
        }
        const observer = new IntersectionObserver(([entry]) => setNearViewport(entry?.isIntersecting === true), {
            rootMargin: IMAGE_LOAD_ROOT_MARGIN,
        })
        observer.observe(target)
        return () => observer.disconnect()
    }, [])

    useEffect(() => {
        if (!attachmentId || !shouldLoad) return
        let disposed = false
        let objectURL: string | null = null
        void downloadNestAttachment(attachmentId)
            .then((downloaded) => {
                if (disposed) return
                objectURL = URL.createObjectURL(new Blob([downloaded.data], { type: image.mediaType ?? downloaded.mediaType }))
                setAttachmentURL(objectURL)
            })
            .catch((error) => {
                console.warn("[ThreadImages] Failed to load attachment", { attachmentId, error })
            })
        return () => {
            disposed = true
            if (!objectURL) return
            URL.revokeObjectURL(objectURL)
            setAttachmentURL((current) => (current === objectURL ? null : current))
        }
    }, [attachmentId, image.mediaType, shouldLoad])

    return (
        <>
            <button
                ref={buttonRef}
                type="button"
                className="h-20 min-w-20 max-w-40 shrink-0 overflow-hidden border border-border bg-base-200 p-0 transition-colors enabled:hover:border-primary/50 disabled:cursor-default"
                disabled={!loadedURL}
                aria-label={image.filename ? `Open ${image.filename}` : "Open attached image"}
                onClick={() => setLightboxOpen(true)}
            >
                {loadedURL ? (
                    <img
                        src={loadedURL}
                        alt={image.filename ?? "Attached image"}
                        className="h-full w-auto max-w-40 object-cover"
                        loading="lazy"
                        decoding="async"
                    />
                ) : (
                    <span className="block h-full w-20 animate-pulse bg-base-300" />
                )}
            </button>
            {lightboxOpen && loadedURL && (
                <ImageLightbox src={loadedURL} {...(image.filename ? { alt: image.filename } : {})} onClose={() => setLightboxOpen(false)} />
            )}
        </>
    )
}

function providerImageURL(image: ThreadImagePart): string | null {
    if (image.source.type !== "provider_uri") return null
    return image.source.uri.startsWith("data:image/") || image.source.uri.startsWith("blob:") ? image.source.uri : null
}

function imageKey(image: ThreadImagePart, index: number): string {
    return image.source.type === "nest_attachment" ? image.source.attachmentId : `${image.source.uri}:${index}`
}
